export const SYNONYM_PREFS_QUERY_KEY = [
  PATH_KEYS.userPreference,
  PreferenceSubPaths.SYNONYM,
] as const;
export const RECENT_SHAREPOINT_SITES_QUERY_KEY = [
  PATH_KEYS.userPreference,
  PreferenceSubPaths.RECENT_SHAREPOINT_SITES,
] as const;
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useItemService } from "./useServices";
import {
  RecentSharePointSitesResponse,
  SynonymPreferences,
} from "@/models/users/UserPreference";
import { SharePointSite } from "@/models/microsoft/CloudLocation";
import { PATH_KEYS } from "@/services/appServices";
import { PreferenceSubPaths } from "@/services/userPreferenceService";
import { logger } from "@/lib/logger";
import { restError } from "@/models/client/RestResponse";

const useUserPreference = () => {
  const queryClient = useQueryClient();
  const userPreferenceService = useItemService(
    (services) => services.userPreference
  );

  const useQuerySynonymPreferences = () =>
    useQuery<SynonymPreferences>({
      queryKey: SYNONYM_PREFS_QUERY_KEY,
      queryFn: async () => {
        const response = await userPreferenceService.getSynonymPreferences();
        if (response.success) return response.value;
        else throw restError(response);
      },
      staleTime: 30000,
      refetchOnWindowFocus: false,
    });

  const updateSynonymPreference = useMutation({
    mutationKey: [
      PATH_KEYS.userPreference,
      PreferenceSubPaths.SYNONYM,
      "update",
    ],
    mutationFn: async ({
      synonymGroupId,
      active,
    }: {
      synonymGroupId: string;
      active: boolean;
    }) => {
      const response = await userPreferenceService.updateSynonymPreference(
        synonymGroupId,
        active
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.synonymGroup, "active-synonym-dict"],
        exact: true,
      });
    },
  });

  const updateSynonymMatching = useMutation({
    mutationKey: [
      PATH_KEYS.userPreference,
      PreferenceSubPaths.SYNONYM,
      "update-matching",
    ],
    mutationFn: async ({ active }: { active: boolean }) => {
      const response =
        await userPreferenceService.updateSynonymMatchingPreference(active);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.synonymGroup, "active-synonym-dict"],
        exact: true,
      });
    },
  });

  /** Recently picked SharePoint sites, newest first (cloud-location chooser). */
  const useQueryRecentSharePointSites = (enabled = true) =>
    useQuery<RecentSharePointSitesResponse>({
      queryKey: RECENT_SHAREPOINT_SITES_QUERY_KEY,
      queryFn: async () => {
        const response = await userPreferenceService.getRecentSharePointSites();
        if (response.success) return response.value;
        const error = restError(response);
        logger.warn("Failed to load recent SharePoint sites", {
          operation: "loadRecentSharePointSites",
          status: response.status,
        });
        throw error;
      },
      enabled,
      retry: false,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    });

  const rememberSharePointSite = useMutation({
    mutationKey: [
      PATH_KEYS.userPreference,
      PreferenceSubPaths.RECENT_SHAREPOINT_SITES,
      "remember",
    ],
    mutationFn: async (site: SharePointSite) => {
      const response = await userPreferenceService.rememberSharePointSite(
        site.webUrl,
        site.displayName
      );
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: RECENT_SHAREPOINT_SITES_QUERY_KEY,
        exact: true,
      });
    },
    onError: (error, site) => {
      // Remembering is best effort; the picker already opened.
      logger.warn("Failed to remember SharePoint site", {
        operation: "rememberSharePointSite",
        webUrl: site.webUrl,
        error,
      });
    },
  });

  return {
    useQuerySynonymPreferences,
    updateSynonymPreference,
    updateSynonymMatching,
    useQueryRecentSharePointSites,
    rememberSharePointSite,
  };
};

export default useUserPreference;
